<template>
  <div class="crm-callback-view">
    <div class="callback-container">
      <div class="callback-card">
        <div class="success-icon">
          <div class="checkmark-circle">
            <div class="checkmark"></div>
          </div>
        </div>

        <h1 class="success-title">CRM Connected Successfully!</h1>
        <p class="success-message">
          Your Zoho CRM integration has been set up successfully.
          You can now sync your call analysis and WhatsApp leads automatically.
        </p>

        <div class="connection-details" v-if="connectionInfo">
          <div class="detail-item">
            <span class="detail-label">Integration ID:</span>
            <span class="detail-value">{{ connectionInfo.integration_id }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Expires:</span>
            <span class="detail-value">{{ formatExpiry(connectionInfo.expires_at) }}</span>
          </div>
        </div>

        <div class="redirect-info">
          <div class="spinner"></div>
          <p>Redirecting to dashboard in {{ countdown }} seconds...</p>
        </div>

        <div class="action-buttons">
          <button @click="goToDashboard" class="btn-primary">
            Go to Dashboard Now
          </button>
          <button @click="goToCRMSettings" class="btn-secondary">
            Configure CRM Settings
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CRMCallbackView',
  data () {
    return {
      countdown: 5,
      connectionInfo: null,
      timer: null
    }
  },
  mounted () {
    this.extractConnectionInfo()
    this.startCountdown()
  },
  beforeUnmount () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    extractConnectionInfo () {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')

      if (code) {
        this.connectionInfo = {
          integration_id: 'updated',
          expires_at: new Date(Date.now() + 3600000).toISOString()
        }
      }

      localStorage.setItem('crm_connection_success', 'true')
      localStorage.setItem('crm_connection_time', Date.now().toString())
    },

    startCountdown () {
      this.timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          this.goToDashboard()
        }
      }, 1000)
    },

    goToDashboard () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.$router.push('/')
    },

    goToCRMSettings () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.$router.push('/crm-integration')
    },

    formatExpiry (expiryDate) {
      if (!expiryDate) return 'Unknown'
      return new Date(expiryDate).toLocaleString()
    }
  }
}
</script>

<style scoped>
.crm-callback-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.callback-container {
  width: 100%;
  max-width: 500px;
}

.callback-card {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-icon {
  margin-bottom: 24px;
}

.checkmark-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #10b981;
  margin: 0 auto;
  position: relative;
  animation: scaleIn 0.5s ease-out 0.2s both;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.checkmark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 35px;
  border: solid white;
  border-width: 0 4px 4px 0;
  transform: translate(-50%, -60%) rotate(45deg);
  animation: checkmarkDraw 0.3s ease-out 0.7s both;
}

@keyframes checkmarkDraw {
  from {
    height: 0;
  }
  to {
    height: 35px;
  }
}

.success-title {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.success-message {
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
}

.connection-details {
  background: #f9fafb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
  text-align: left;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 500;
  color: #6b7280;
}

.detail-value {
  font-weight: 600;
  color: #111827;
  font-family: monospace;
  font-size: 14px;
}

.redirect-info {
  margin-bottom: 32px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  border-top-color: #667eea;
  animation: spin 1s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.redirect-info p {
  color: #6b7280;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-direction: column;
}

@media (min-width: 480px) {
  .action-buttons {
    flex-direction: row;
    justify-content: center;
  }
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  max-width: 200px;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: #e5e7eb;
  transform: translateY(-1px);
}

/* Compact mobile view */
@media (max-width: 480px) {
  .callback-card {
    padding: 30px 20px;
  }

  .success-title {
    font-size: 24px;
  }

  .checkmark-circle {
    width: 60px;
    height: 60px;
  }

  .checkmark {
    width: 15px;
    height: 25px;
  }
}
</style>
